import { Box, useTheme, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import * as yup from 'yup';
import Alert from '@mui/material/Alert';
import Cookies from 'universal-cookie';
import { API_POST_NO_TOKEN, DBRequest, useConfig } from '../api/api';
import Loader from '../components/special/Loader';
import { tokens } from '../global/theme/tokens';
import ExoForm from '../components/exo/ExoForm';
import { useTranslation } from 'react-i18next';
import { subscribeForPush } from '../components/special/usePushNotifications';
import Div100vh from 'react-div-100vh';

const checkoutSchema = yup.object().shape({
  password: yup.string().required('required'),
  email: yup.string().email('invalid email').required('required'),
});

/**
 * StandaloneLogin is an embedded login component for standalone apps.
 * Instead of redirecting after login, it calls the onLoginSuccess callback.
 */
const StandaloneLogin = ({ onLoginSuccess, appName = 'App', appLogo }) => {
  const cookies = new Cookies();
  const current = new Date();
  const nextYear = new Date();
  nextYear.setFullYear(current.getFullYear() + 1);

  const [errMsg, setErrMsg] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const colorMode = cookies.get('colorMode');

  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();
  const config = useConfig();

  const [dataCollection, setDataCollection] = useState({});

  const handleFormSubmit = (data) => {
    if (!config) return;

    setIsLoading(true);

    DBRequest({
      config,
      customPath: true,
      path: config.API_LOGIN,
      method: API_POST_NO_TOKEN(data),
      onResponse: (e) => handleInitilization(e.data),
      onError: handleLoginFailed,
    });
  };

  function handleLoginFailed(error) {
    setIsLoading(false);
    setErrMsg(`${error.message}`);
  }

  async function handleInitilization(data) {
    handleSetCookies(data);
    if (data?.vapid?.publicKey) {
      await subscribeForPush(config, data.vapid.publicKey);
    }
    setIsLoading(false);
    // Call the success callback instead of redirecting
    if (onLoginSuccess) {
      onLoginSuccess();
    }
  }

  function handleSetCookies(data) {
    setErrMsg('');
    try {
      cookies.set('accessToken', data.token, { path: '/', expires: nextYear });
      cookies.set('userId', data.user.id, { path: '/', expires: nextYear });
      cookies.set('firstName', data.user.firstName, {
        path: '/',
        expires: nextYear,
      });
      cookies.set('lastName', data.user.lastName, {
        path: '/',
        expires: nextYear,
      });
      if (data.user.picture) {
        cookies.set('profilePicture', data.user.picture.temporaryUrl, {
          path: '/',
          expires: nextYear,
        });
      }
      if (data.user) {
        cookies.set('payType', data.user.payType, {
          path: '/',
          expires: nextYear,
        });
        cookies.set('hoursPerDay', data.user.hoursPerDay, {
          path: '/',
          expires: nextYear,
        });
      }
      // preferences
      cookies.set(
        'colorTheme',
        data.user.preferences.colorTheme
          ? data.user.preferences.colorTheme
          : 'EMPTY',
        { path: '/', expires: nextYear }
      );
      cookies.set('colorMode', data.user.preferences.colorMode, {
        path: '/',
        expires: nextYear,
      });
      if (data.user.preferences.backgroundImageDark) {
        cookies.set(
          'backgroundImageDark',
          data.user.preferences.backgroundImageDark.temporaryUrl,
          { path: '/', expires: nextYear }
        );
      }
      if (data.user.preferences.backgroundImageLight) {
        cookies.set(
          'backgroundImageLight',
          data.user.preferences.backgroundImageLight.temporaryUrl,
          { path: '/', expires: nextYear }
        );
      }
      if (data.search) {
        cookies.set('searchToken', data.search.token, {
          path: '/',
          expires: nextYear,
        });
        cookies.set('searchIndexes', data.search.allowedIndexes, {
          path: '/',
          expires: nextYear,
        });
        cookies.set('searchPrefix', data.search.prefix, {
          path: '/',
          expires: nextYear,
        });
      }
      cookies.set('personalData', data.personalData, {
        path: '/',
        expires: nextYear,
      });
      // permissions
      if (data.permissions) {
        cookies.set('permissions', data.permissions, {
          path: '/',
          expires: nextYear,
        });
      } else {
        cookies.set(
          'permissions',
          { admin: '*' },
          { path: '/', expires: nextYear }
        );
      }
      if (data.vapid.publicKey) {
        cookies.set('pushKey', data.vapid.publicKey, {
          path: '/',
          expires: nextYear,
        });
      }
      if (data.calendarServicesEnabled) {
        cookies.set('calendarServicesEnabled', data.calendarServicesEnabled, {
          path: '/',
          expires: nextYear,
        });
      }
    } catch (err) {
      console.error('ERROR: setting cookies', err);
    }
  }

  useEffect(() => {
    const handleEnter = (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        e.stopPropagation();

        const isValid = checkoutSchema.isValidSync({
          email: dataCollection?.email,
          password: dataCollection?.password,
        });

        if (isValid) {
          handleFormSubmit({
            email: dataCollection.email,
            password: dataCollection.password,
          });
        }
      }
    };

    window.addEventListener('keydown', handleEnter);
    return () => {
      window.removeEventListener('keydown', handleEnter);
    };
  }, [dataCollection]);

  const fields = [
    {
      label: t('E-Mail'),
      key: 'email',
      type: 'text',
    },
    {
      label: t('Password'),
      key: 'password',
      type: 'password',
    },
  ];

  return (
    <Div100vh>
      <Box className="h-full flex justify-center items-center relative">
        <img
          src={
            colorMode === 'dark'
              ? '/assets/uploads/background-dark.webp'
              : '/assets/uploads/background-bright.webp'
          }
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            position: 'absolute',
            top: '0',
            left: '0',
            objectPosition: '32%',
          }}
          alt="Background"
        />
        <Box
          sx={{ bgcolor: colors.glass }}
          className="backdrop-blur-lg rounded-3xl w-80 p-4 md:w-90 flex flex-col gap-5 justify-center"
        >
          {appLogo ? (
            <img src={appLogo} className="w-60 h-16 m-auto" alt={appName} />
          ) : (
            <Typography
              variant="h3"
              fontWeight="600"
              sx={{ color: colors.text, textAlign: 'center', mb: 2 }}
            >
              {appName}
            </Typography>
          )}
          {errMsg !== '' && <Alert severity="error">{errMsg}</Alert>}
          <ExoForm
            validationSchema={checkoutSchema}
            fields={fields}
            submitText={t('Login')}
            onChange={(data) => setDataCollection(data)}
            sx={{
              button: {
                width: '100%',
                backgroundColor: colors.bgInput,
                fontSize: '15px',
              },
            }}
            onSubmit={handleFormSubmit}
          />
        </Box>
        <Loader active={isLoading} type="modern" />
      </Box>
    </Div100vh>
  );
};

export default StandaloneLogin;
